import { Box, Tooltip, tooltipClasses, TooltipProps, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { IconCross, IconQR } from 'assets/img';
import { FlexPoolOnlyIndicator } from 'shared/components/common/flexpool-only-indicator';
import { ReactNode } from 'react';
import { ShiftDetail } from 'api/services/shift/schema/shift-detail.schema';
import { ContextMenuButton, LwIconButton } from 'redesign';
import { useDownloadQRCodePDF } from 'shared/components/qr-code/hooks/use-download-qr-code-pdf/use-download-qr-code-pdf';
import { LevelQrCodeCanvas } from 'shared/components/qr-code/level-qr-code-canvas/level-qr-code-canvas';
import { pickShiftPDFOptions } from 'shared/components/qr-code/utils/pick-shift-pdf-options';
import { useQrCodeDownloadGuideDialog } from 'shared/components/qr-code/qr-code-download-dialog/use-qr-code-download-guide-dialog';
import { getQrCheckoutDeeplinkUrl } from './utils/get-qr-checkout-deeplink-url';
import { ShiftDetailHeadPropertyKey } from './shift-detail-head.types';
import { useGetShiftDetailHeadProperties } from './get-shift-detail-head-properties';
import { ShiftDetailHeadPropertyItem } from './shift-detail-head-property-item';

const FULL_SHIFT_DETAIL_PROPERTY_COLUMNS: ShiftDetailHeadPropertyKey[][] = [
  ['seats', 'startDate', 'cooldownPeriod'],
  ['duration', 'break', 'flexPoolNames'],
  ['hourlyRates', 'totalRate', 'certificates'],
  ['cancelationPeriod', 'location', 'description'],
];

const LIMITED_SHIFT_DETAIL_PROPERTY_COLUMNS: ShiftDetailHeadPropertyKey[][] = [
  ['seats', 'startDate'],
  ['duration', 'break'],
  ['certificates', 'cancelationPeriod'],
  ['location', 'description'],
];

export const ShiftDetailHead = ({
  data,
  close,
  actions,
  isArchiving,
  showTempWorkRate,
  hideLimitedCompanyShiftFields = false,
}: {
  data: ShiftDetail;
  close: () => void;
  actions: ReactNode;
  isArchiving: boolean;
  showTempWorkRate: boolean;
  hideLimitedCompanyShiftFields?: boolean;
}) => {
  const shiftProperties = useGetShiftDetailHeadProperties(
    data,
    showTempWorkRate,
    hideLimitedCompanyShiftFields
  );

  const { canvasRef, handleDownload } = useDownloadQRCodePDF({
    ...pickShiftPDFOptions(data),
  });

  const { Element: DownloadGuideDialog, open: openDownloadGuideDialog } =
    useQrCodeDownloadGuideDialog({
      onDownload: handleDownload,
    });

  return (
    <Box>
      <LevelQrCodeCanvas
        canvasRef={canvasRef}
        targetUrl={getQrCheckoutDeeplinkUrl(data.id, { isOpusCompany: data.isCompanyOpus })}
      />
      <Box display="flex" flexDirection="row">
        <CompanyInfoBox>
          <Typography
            noWrap
            color="secondary"
            variant="body1"
            sx={{ fontWeight: 'bold', textOverflow: 'ellipsis' }}
          >
            {shiftProperties.businessUnitInfo}
          </Typography>
          {hideLimitedCompanyShiftFields ? (
            <FullWidthTooltip title={shiftProperties.jobName} placement="bottom-start">
              <StyledJobName noWrap variant="h5">
                {shiftProperties.jobName}
              </StyledJobName>
            </FullWidthTooltip>
          ) : (
            <FlexPoolOnlyIndicator flexPoolOnly={data.flexPoolOnly}>
              <FullWidthTooltip title={shiftProperties.jobName} placement="bottom-start">
                <StyledJobName noWrap variant="h5">
                  {shiftProperties.jobName}
                </StyledJobName>
              </FullWidthTooltip>
            </FlexPoolOnlyIndicator>
          )}
        </CompanyInfoBox>
        <Box display="flex" flexDirection="row" ml="auto" alignItems="middle" style={{ gap: 8 }}>
          <LwIconButton
            iconColorMode="stroke"
            color="secondary"
            onClick={openDownloadGuideDialog}
            data-testid="download-qr-code-button"
          >
            <IconQR width={24} height={24} />
          </LwIconButton>
          <ContextMenuButton loading={isArchiving} size="medium" color="secondary">
            {actions}
          </ContextMenuButton>
          <LwIconButton iconColorMode="stroke" color="secondary" onClick={close}>
            <IconCross width={24} height={24} />
          </LwIconButton>
        </Box>
      </Box>

      <PropertiesBox data-testid="shift-detail-properties">
        {(hideLimitedCompanyShiftFields
          ? LIMITED_SHIFT_DETAIL_PROPERTY_COLUMNS
          : FULL_SHIFT_DETAIL_PROPERTY_COLUMNS
        ).map((column, columnIndex) => (
          <InfoLabelBox data-testid="shift-detail-property-column" key={columnIndex}>
            {column.map((propertyKey) => (
              <ShiftDetailHeadPropertyItem
                key={propertyKey}
                data={data}
                propertyKey={propertyKey}
                shiftProperties={shiftProperties}
                showTempWorkRate={showTempWorkRate}
                hideLimitedCompanyShiftFields={hideLimitedCompanyShiftFields}
              />
            ))}
          </InfoLabelBox>
        ))}
      </PropertiesBox>
      {DownloadGuideDialog}
    </Box>
  );
};

const StyledJobName = styled(Typography)(({ theme }) => ({
  flex: 1,
  marginTop: theme.spacing(2),
}));

const CompanyInfoBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

const InfoLabelBox = styled(CompanyInfoBox)(() => ({
  flex: 1,
  minWidth: 0,
}));

const PropertiesBox = styled(Box)(({ theme }) => ({
  gap: theme.spacing(3),
  display: 'flex',
  flexDirection: 'row',
  marginTop: theme.spacing(5),
  '& svg': {
    opacity: 0.6,
  },
}));

const FullWidthTooltip = styled((props: TooltipProps) => {
  const { className, ...rest } = props;
  return <Tooltip {...rest} classes={{ popper: className }} />;
})(() => ({
  flex: 1,
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 'none',
  },
})) as React.ComponentType<TooltipProps>;
